import { useEffect, useRef } from 'react';
import useDragPreviewManagerValue from 'layout-dnd-utils/DragPreview/useDragPreviewManagerValue';
import { useDragPreviewManager } from 'layout-dnd-utils/DragPreview/DragPreviewManager';
import { clampDragPreviewPosition } from 'layout-dnd-utils/DragPreview/clampDragPreviewPosition';
import { useTransitionStyles } from 'layout-dnd-utils/TransitionStyles/TransitionStylesContext';
const useScrollPosition = () => {
  const scrollPositionRef = useRef({
    initialLeft: window.scrollX,
    initialTop: window.scrollY,
    adjustedLeft: 0,
    adjustedTop: 0,
    currentLeft: window.scrollX,
    currentTop: window.scrollY
  });
  useEffect(() => {
    const handleScroll = () => {
      scrollPositionRef.current.adjustedLeft = window.scrollX - scrollPositionRef.current.initialLeft;
      scrollPositionRef.current.adjustedTop = window.scrollY - scrollPositionRef.current.initialTop;
      scrollPositionRef.current.currentLeft = window.scrollX;
      scrollPositionRef.current.currentTop = window.scrollY;
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  return scrollPositionRef.current;
};
/**
 * Renders a shadowed drag preview of the element to be dragged, with the same
 * size and position as the dragged element. The dragged element is updated to
 * then follow the cursor position when the cursor is moved.
 */
const DragPreview = ({
  scale = 1,
  element,
  sourceClientOffset,
  differenceFromInitialOffset,
  pegboardBounds
}) => {
  const dragPreviewManager = useDragPreviewManager();
  const transitionStylesManager = useTransitionStyles();
  const zIndex = useDragPreviewManagerValue(_dragPreviewManager => _dragPreviewManager.getDragPreviewZIndex());
  const {
    adjustedLeft,
    adjustedTop,
    currentLeft,
    currentTop
  } = useScrollPosition();
  useEffect(() => {
    if (element) {
      transitionStylesManager.registerTransitionStyle(element, 'transform');
      transitionStylesManager.registerTransitionStyle(element, 'z-index');
    }
  }, [element, transitionStylesManager]);

  // Updates the dragged element to follow the cursor position when the cursor
  // is moved. It's adjusted to account for a cursor offset and scroll position.
  useEffect(() => {
    if (element && differenceFromInitialOffset && sourceClientOffset) {
      let deltaX = 0;
      let deltaY = 0;
      if (pegboardBounds) {
        const rect = element.getBoundingClientRect();
        ({
          deltaX,
          deltaY
        } = clampDragPreviewPosition(sourceClientOffset.x, sourceClientOffset.y, rect.width, rect.height, pegboardBounds));
      }
      const x = (differenceFromInitialOffset.x + adjustedLeft + deltaX) / scale;
      const y = (differenceFromInitialOffset.y + adjustedTop + deltaY) / scale;
      dragPreviewManager.setDragPreviewPosition({
        elementLeft: sourceClientOffset.x + deltaX + currentLeft,
        elementTop: sourceClientOffset.y + deltaY + currentTop
      });
      element.style.transform = `translate(${x}px, ${y}px)`;
      element.style.zIndex = zIndex;
    }
  }, [element, adjustedLeft, adjustedTop, currentLeft, currentTop, zIndex, scale, differenceFromInitialOffset, sourceClientOffset, dragPreviewManager, pegboardBounds]);
  return null;
};
export default DragPreview;